"use client";

import { useState, useEffect } from "react";
import { ChevronDown, Mail, Phone, MapPin, Clock1 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { MapLocation } from "@/types/location";
import { useTranslations } from "next-intl";

interface LocationCardProps {
  location: MapLocation;
  isExpanded?: boolean;
  onToggle?: () => void;
  className?: string;
  "data-location-id"?: string;
}

export function LocationCard({
  location,
  isExpanded = false,
  onToggle,
  className,
  "data-location-id": dataLocationId,
}: LocationCardProps) {
  // Only use internal state if no external control is provided
  const [internalExpanded, setInternalExpanded] = useState(isExpanded);

  // Update internal state when external isExpanded changes
  useEffect(() => {
    if (onToggle) {
      setInternalExpanded(isExpanded);
    }
  }, [isExpanded, onToggle]);

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  const isOpen = onToggle ? isExpanded : internalExpanded;
  const t = useTranslations('locationsPage.card');

  return (
    <div
      data-location-id={dataLocationId}
      className={cn(
        "font-outfit bg-site-card p-6 rounded-[20px] transition-all duration-200 border border-transparent",
        isOpen && "border-yellow-500/50 shadow-lg shadow-yellow-500/20",
        className
      )}
    >
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between text-left rounded-lg p-2 -m-2 transition-colors"
      >
        <div className="flex-1">
          <h3 className="text-xl font-bold text-yellow-500">{location.name}</h3>
          {!isOpen && (
            <p className="text-gray-400 text-sm mt-1">
              {typeof location.address === 'object'
                ? `${location.address.street}, ${location.address.city}`
                : location.address}
            </p>
          )}
        </div>
        <div className={cn(
          "transition-transform duration-200 flex-shrink-0 ml-2",
          isOpen && "rotate-180"
        )}>
          <ChevronDown className="h-5 w-5 text-yellow-500" />
        </div>
      </button>

      <div className={cn(
        "overflow-hidden transition-all duration-300 ease-in-out",
        isOpen ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
      )}>
        <div className="pb-6 space-y-6 mt-4">
          {/* Email */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Mail className="h-4 w-4 text-yellow-500" />
              <span className="text-white font-medium">{t('email')}</span>
            </div>
            <a
              href={`mailto:${location.email}`}
              className="text-white hover:text-yellow-500 transition-colors"
            >
              {location.email}
            </a>
          </div>

          {/* Phone */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Phone className="h-4 w-4 text-yellow-500" />
              <span className="text-white font-medium">{t('phone')}</span>
            </div>
            {location.phone && (
              <a
                href={location.phone.telHref}
                data-phone={location.phone.key}
                className="text-white hover:text-yellow-500 transition-colors"
              >
                {location.phone.display}
              </a>
            )}
          </div>

          {/* Address */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="h-4 w-4 text-yellow-500" />
              <span className="text-white font-medium">{t('address')}</span>
            </div>
            <div className="text-white space-y-1">
              <div>
                {typeof location.address === "object" ? location.address.street : location.address}
              </div>
              <div>
                {typeof location.address === "object"
                  ? `${location.address.city}, ${location.address.state}`
                  : ""}
              </div>
              <div>
                {typeof location.address === "object" ? location.address.country : ""}
              </div>
            </div>
          </div>

          {/* Hours of operation */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Clock1 className="h-4 w-4 text-yellow-500" />
              <span className="text-white font-medium">{t('hoursTitle')}</span>
            </div>
            <div className="text-white">
              <div>
                <span className="text-white">{t('monday')}</span>{" "}
                <span className="text-yellow-500">
                  {location.hours?.mondayFriday}
                </span>
              </div>
              <div>
                <span className="text-white">{t('saturday')}</span>{" "}
                <span className="text-yellow-500">
                  {location.hours?.saturday}
                </span>
              </div>
              <div>
                <span className="text-white">{t('sunday')}</span>{" "}
                <span className="text-yellow-500">
                  {location.hours?.sunday}
                </span>
              </div>
            </div>
          </div>

          {/* Services */}
          {(location.services?.length ?? 0) > 0 && (
            <div>
              <span className="text-white font-medium mb-2 block">{t('services')}</span>
              <div className="flex flex-wrap gap-2">
                {(location.services ?? []).map((service) => (
                  <span
                    key={service}
                    className="px-3 py-1 bg-zinc-800 text-white text-sm rounded-full hover:bg-zinc-700 transition-colors"
                  >
                    {service}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
